/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../../../../components/Modal'
import { useModal } from '../../../../contexts/ModalContext'
import AddDocumentModal from './AddDocumentModal'
import AddNewPlaceholderModal from './AddNewPlaceholderModal'
import AddAliasModal from './AddAliasModal'

interface FsiObject {
  id: number
  name: string
  systemFolderType?: string
  templateId?: number
}

interface AddDocChoiceModalProps {
  parentFsi: FsiObject
  onClose?: () => void
}

type DocChoiceType = 'REGULAR' | 'PLACEHOLDER' | 'ALIAS'

export function AddDocChoiceModal({ parentFsi, onClose }: AddDocChoiceModalProps) {
  const { t } = useTranslation()
  const { showModal } = useModal()
  const [isOpen, setIsOpen] = useState(true)
  const [docChoice, setDocChoice] = useState<DocChoiceType>('REGULAR')

  const submit = () => {
    // Close current modal first
    doClose()

    // Show the appropriate modal based on selection
    switch (docChoice) {
      case 'REGULAR':
        showModal(<AddDocumentModal parentId={parentFsi?.id} />)
        break
      case 'PLACEHOLDER':
        showModal(<AddNewPlaceholderModal parentFsi={parentFsi} />)
        break
      case 'ALIAS':
        showModal(<AddAliasModal parentFsi={parentFsi} />)
        break
    }
  }

  const doClose = () => {
    setIsOpen(() => false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      size="lg"
      id="modal-add-file"
      className="doc-choice-modal-wrapper theme-sandman"
      title={t('BROWSER_ADD_DOCUMENT_H_TITLE')}
    >
      <Modal.Body>
        <form name="AddDocumentChoiceForm">
          <div className="form-group md-form-group checkbox-form-group w100p">
            <input
              type="radio"
              id="input-n"
              name="docClassification"
              value="REGULAR"
              checked={docChoice === 'REGULAR'}
              onClick={() => {
                setDocChoice('REGULAR')
              }}
              onChange={() => {
                // Empty handler to prevent warning
              }}
            />
            <label htmlFor="input-n">
              <div className="chkbc"></div>
              <div className="doc-choice-type">{t('BROWSER_ADD_DOCUMENT_REGULAR_DOCUMENT_LABEL')}</div>
              <small className="help-text">{t('BROWSER_ADD_DOCUMENT_REGULAR_DOCUMENT_INFO')}</small>
            </label>
          </div>

          <div className="form-group md-form-group checkbox-form-group w100p">
            <input
              type="radio"
              id="input-p"
              name="docClassification"
              value="PLACEHOLDER"
              checked={docChoice === 'PLACEHOLDER'}
              onClick={() => {
                setDocChoice('PLACEHOLDER')
              }}
              onChange={() => {
                // Empty handler to prevent warning
              }}
            />
            <label htmlFor="input-p">
              <div className="chkbc"></div>
              <div className="doc-choice-type">{t('BROWSER_ADD_DOCUMENT_PLACEHOLDER_LABEL')}</div>
              <small className="help-text">{t('BROWSER_ADD_DOCUMENT_PLACEHOLDER_INFO')}</small>
            </label>
          </div>

          <div className="form-group md-form-group checkbox-form-group w100p">
            <input
              type="radio"
              id="input-y"
              name="docClassification"
              value="ALIAS"
              checked={docChoice === 'ALIAS'}
              onClick={() => {
                setDocChoice('ALIAS')
              }}
              onChange={() => {
                // Empty handler to prevent warning
              }}
            />
            <label htmlFor="input-y">
              <div className="chkbc"></div>
              <div className="doc-choice-type">{t('BROWSER_ADD_DOCUMENT_REFERENCE_LABEL')}</div>
              <small className="help-text">{t('BROWSER_ADD_DOCUMENT_REFERENCE_INFO')}</small>
            </label>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-close" type="button" onClick={doClose}>
          {t('CANCEL')}
        </button>
        <button className="btn btn-primary" type="button" onClick={submit}>
          {t('NEXT')}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddDocChoiceModal
